import { useEffect, type RefObject } from 'react';

/**
 * What to watch for content growth: the wrapper and its children, and the
 * children of a lone child. A lone child's own box is stretched to the capped
 * wrapper, so once it fills the room it stops changing size as its content
 * grows, but what it holds is never clamped and keeps reporting.
 *
 * The depth is deliberate: growth one level deeper, inside a box that is itself
 * stretched or capped, resizes nothing observed and is not seen.
 */
const getContentTargets = (floating: HTMLElement) => {
  const wrapper = floating.firstElementChild;
  if (!wrapper) return { targets: [], containers: [] };

  const loneChild =
    wrapper.childElementCount === 1 ? wrapper.firstElementChild : null;

  return {
    targets: [wrapper, ...wrapper.children, ...(loneChild?.children ?? [])],
    containers: loneChild ? [wrapper, loneChild] : [wrapper],
  };
};

/**
 * Re-runs `reposition` while the surface is open whenever its content changes
 * size, so content that grows past the room after open is capped and scrolls
 * like content that was too tall at open, and a `top` or `left` surface stays
 * against its trigger as it grows or shrinks.
 *
 * The pass runs in the next frame rather than in the observer callback: it
 * writes the caps and the overflow flags, which resize what is observed (a
 * scrollbar appearing narrows the children), and a size change made inside the
 * callback above the depth just delivered ends the frame with the browser's
 * "loop completed with undelivered notifications" error. It settles because a
 * pass with the same content and trigger writes the same values: it measures
 * the surface at the origin rather than where the last pass left it (see
 * `applyFloatingPosition`), so the frame after a pass that changed nothing has
 * nothing new to report.
 *
 * A pass waits until the root's own animations finish: the open animation
 * scales the root, and a pass in it measures the scaled box (in Chromium a
 * 322px popover reads as 307px one frame in) and places the surface short of
 * its trigger for good. It waits by re-arming the frame, not by awaiting
 * `finished`, so the wait is one cancellable handle like everything else here.
 *
 * Until the pass runs, a `top` or `left` surface that grew paints over its
 * trigger, since it is anchored by `top`/`left` and grows away from that edge:
 * one frame normally, the whole open animation while the pass waits (20 frames
 * at 120 Hz on `Tooltip/Default`). That fires `mouseleave` on the trigger, and a
 * tooltip survives it only because its surface's `mouseenter` calls
 * `cancelClose`, so the hoverable surface is load-bearing for this hook too.
 *
 * Observed targets are re-collected whenever the wrapper's or the lone child's
 * children change, since rows appended to a lone child already at the room
 * resize nothing that was observed, and a new lone child is a new box.
 */
export const useRepositionOnContentResize = (
  floatingRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  reposition: () => void,
) => {
  useEffect(() => {
    const floating = floatingRef.current;
    if (!isOpen || !floating || typeof ResizeObserver === 'undefined') return;

    let frame = 0;

    // One frame pending at most: a report while one is pending adds nothing,
    // since the pass reads the content when it runs. While the root animates
    // the frame re-arms itself rather than passing, which keeps the wait
    // cancellable from the cleanup below.
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const isAnimating = floating
          .getAnimations()
          .some((animation) => animation.playState === 'running');
        if (isAnimating) schedule();
        else reposition();
      });
    };

    const resizeObserver = new ResizeObserver(schedule);
    const mutationObserver = new MutationObserver(() => observe());

    const observe = () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();

      const { targets, containers } = getContentTargets(floating);
      for (const target of targets) resizeObserver.observe(target);
      for (const container of containers) {
        mutationObserver.observe(container, { childList: true });
      }
    };

    observe();

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [floatingRef, isOpen, reposition]);
};
